import { useCallback, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import { useLoaderStore } from '@/store/loader'

import { TransitionMaskHandler } from './components/transition-mask'
import { useTransitionStore } from './use-transition-store'

export const useTransition = () => {
  const nodeRef = useRef<HTMLElement>(null!)
  const maskRef = useRef<TransitionMaskHandler>(null!)
  const { contextSafe } = useGSAP({ scope: nodeRef })
  const setLoaderState = useLoaderStore((state) => state.setState)
  const setTransitionState = useTransitionStore((state) => state.setState)
  const needsMask = useRef(true)
  const active = useRef(false)

  const isLoaded = useCallback(
    () => useLoaderStore.getState().pageLoaded && document.fonts.status === 'loaded',
    []
  )

  const loaderStateWatcher = () =>
    new Promise<void>((resolve) => {
      const tick = () => {
        if (isLoaded()) {
          gsap.ticker.remove(tick)
          resolve()
        }
      }
      gsap.ticker.add(tick)
    })

  const needsMaskTransition = useCallback(() => {
    return !(
      nodeRef.current.dataset.route === 'search' || nodeRef.current.dataset.route === 'liveroom'
    )
  }, [])

  const handleLeaveTransition = contextSafe(() => {
    active.current = false

    needsMask.current = needsMaskTransition()
    setLoaderState({ pageLoaded: false })
    setTransitionState({ active: true })
  })

  const handleEnterTransition = contextSafe(() => {
    active.current = true
    needsMask.current = needsMaskTransition()
  })

  const handleEndListener = contextSafe(async (done: () => void) => {
    if (!active.current) {
      needsMask.current
        ? await maskRef.current?.enterTimeline()
        : await gsap
            .timeline()
            .to(nodeRef.current.childNodes, { opacity: 0, duration: 0.5, ease: 'power2.inOut' })
      done()
    } else if (isLoaded()) {
      setTransitionState({ reveal: true })
      // console.log(needsMask.current, nodeRef.current.childNodes)
      needsMask.current
        ? await maskRef.current?.leaveTimeline()
        : await gsap
            .timeline()
            .fromTo(
              nodeRef.current.childNodes,
              { opacity: 0 },
              { opacity: 1, duration: 0.5, ease: 'power2.inOut' }
            )
      setTransitionState({ active: false, reveal: false })
      done()
    } else {
      console.log('Waiting for loader state...')
      needsMask.current
        ? maskRef.current?.resetTimeline()
        : gsap.set(nodeRef.current.childNodes, { opacity: 0 })
      await loaderStateWatcher()
      handleEndListener(done)
    }
  })

  return { nodeRef, maskRef, handleEnterTransition, handleLeaveTransition, handleEndListener }
}
